import React from 'react';
import { Crown, AlertTriangle, TrendingUp } from 'lucide-react';
import { SkillNewsUsageResponse } from '../../services/skillNewsService';
import { Button } from '../common/Button';

interface SkillNewsUsageCardProps {
  usage: SkillNewsUsageResponse;
  className?: string;
  onUpgrade?: () => void;
  compact?: boolean;
}

const SkillNewsUsageCard: React.FC<SkillNewsUsageCardProps> = ({ usage, className = '', onUpgrade, compact = false }) => {
  const getProgressWidth = () => {
    return `${(usage.usageCount / usage.monthlyLimit) * 100}%`;
  };

  const getProgressColor = () => {
    if (usage.remainingUsage === 0) return 'bg-red-500';
    if (usage.remainingUsage <= 2) return 'bg-yellow-500';
    return 'bg-amber-500'; // Use amber to match alert style
  };

  const getAlertColor = () => {
    if (usage.remainingUsage === 0) return 'red';
    if (usage.remainingUsage <= 2) return 'yellow';
    return 'amber';
  };

  const alertColor = getAlertColor();
  const isMobileView = window.innerWidth < 768;

  return (
    <div className={`p-4 rounded-xl border border-${alertColor}-500/20 dark:border-${alertColor}-400/20 bg-${alertColor}-500/5 dark:bg-${alertColor}-400/5 backdrop-blur-sm ${className}`}>
      <div className={`flex ${isMobileView ? 'flex-col items-start' : 'items-center'}`}>
        <div className={`p-2 rounded-full bg-${alertColor}-500/10 dark:bg-${alertColor}-400/10 ${isMobileView ? 'mb-3 w-10 h-10' : 'mr-4'}`}>
          <AlertTriangle className={`w-6 h-6 text-${alertColor}-500 dark:text-${alertColor}-400`} />
        </div>
        <div className={`flex-1 ${isMobileView ? 'mb-4' : ''}`}>
          <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
            {usage.remainingUsage === 0 ? 'Monthly Skill News Limit Reached' : 'Skill News Limit'}
          </h3>
          <p className="mt-1 text-text-secondary-light dark:text-text-secondary-dark text-sm">
            {usage.remainingUsage === 0 
              ? `You've used all ${usage.monthlyLimit} days this month. Subscribe to a plan to get unlimited access.`
              : <span>You get <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{usage.monthlyLimit} days</span> of skill-based news for free. Subscribe to a plan to get unlimited access.</span>
            }
          </p>
          
          {/* Progress Bar */}
          <div className="mt-3 space-y-2 max-w-xl">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary-light dark:text-text-secondary-dark">
                Days Used: <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{usage.usageCount}/{usage.monthlyLimit}</span>
              </span>
              <span className={`font-medium text-${alertColor}-600 dark:text-${alertColor}-400`}>
                {usage.remainingUsage} remaining
              </span>
            </div>
            <div className="w-full bg-surface-light dark:bg-surface-dark rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                style={{ width: getProgressWidth() }}
              />
            </div>
          </div>
          <p className="mt-3 text-text-secondary-light dark:text-text-secondary-dark text-sm">
            Limit resets on <span className="text-text-primary-light dark:text-text-primary-dark font-medium">{new Date(usage.year, usage.month).toLocaleDateString('en-US', { 
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span> or subscribe to get unlimited access.
          </p>
        </div>
        
        {onUpgrade && (
          <Button 
            variant="secondary" 
            size="sm"
            className={`whitespace-nowrap py-3 ${isMobileView ? 'self-start w-full' : 'ml-4'}`}
            onClick={onUpgrade}
            leftIcon={<Crown className="w-4 h-4" />}
            trackingId="skill-news-upgrade"
            trackingLocation="skill-news-usage-alert"
          >
            Get Unlimited Access
          </Button>
        )}
      </div>
    </div>
  );
};

export default SkillNewsUsageCard; 